/**
 * Pick a "display column" for a referenced (parent) table — used by the FK
 * lookup picker to render rows like `42 — Acme Corp` instead of just `42`.
 *
 * Heuristic only: scans column names in priority order, case-insensitive, and
 * returns the first match. Returns null when no candidate matches; callers
 * should then fall back to showing the raw FK value with no label.
 */

const CANDIDATE_NAMES = [
  'name',
  'display_name',
  'displayname',
  'full_name',
  'fullname',
  'title',
  'label',
  'description',
  'code',
  'slug',
  'email',
  'username',
  'user_name',
] as const;

/**
 * Pick a display column from a list of column names.
 *
 * @param columnNames - Column names from the parent table.
 * @param fkColumn - The FK target column to exclude from candidates (we never
 *   want to "label" the FK value with itself).
 * @returns The first matching column name (preserving original casing) or null.
 */
export function pickDisplayColumn(
  columnNames: readonly string[],
  fkColumn?: string,
): string | null {
  if (!columnNames || columnNames.length === 0) return null;

  const fkLower = fkColumn?.toLowerCase();
  const lowerToOriginal = new Map<string, string>();
  for (const original of columnNames) {
    const lower = original.toLowerCase();
    if (lower === fkLower) continue;
    if (!lowerToOriginal.has(lower)) {
      lowerToOriginal.set(lower, original);
    }
  }

  for (const candidate of CANDIDATE_NAMES) {
    const match = lowerToOriginal.get(candidate);
    if (match) return match;
  }

  return null;
}
